import { useEffect, useMemo, useState } from 'react';
import { Copy, Download, Share2, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function buildCardUrl(d) {
    const base = `${BACKEND_URL}/api/share/card/${d.card}`;
    const params = new URLSearchParams();
    Object.entries(d).forEach(([k, v]) => {
        if (k === 'card') return;
        if (v === null || v === undefined || v === '') return;
        params.set(k, String(v));
    });
    return `${base}?${params.toString()}`;
}

function shareLine(d) {
    if (d.card === 'streak') return `${d.days}-day meditation streak on Quiet Mind 🌿`;
    if (d.card === 'challenge') return `Just finished "${d.title}" on Quiet Mind — ${d.reward} 🌿`;
    if (d.card === 'minutes') return `${d.minutes} minutes of quiet on Quiet Mind.`;
    if (d.card === 'session') return `Just sat with "${d.title}" (${d.minutes}m) on Quiet Mind.`;
    return 'Quiet Mind';
}

export default function ShareModal({ data, onClose }) {
    const url = useMemo(() => buildCardUrl(data), [data]);
    const [copied, setCopied] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const canWebShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    useEffect(() => {
        const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onEsc);
        return () => document.removeEventListener('keydown', onEsc);
    }, [onClose]);

    const appUrl = typeof window !== 'undefined' ? window.location.origin : '';

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(`${shareLine(data)}\n${appUrl}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    };

    const download = async () => {
        setDownloading(true);
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            const href = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = href; a.download = `quietmind-${data.card}.png`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(href);
        } finally { setDownloading(false); }
    };

    const nativeShare = async () => {
        try {
            if (!canWebShare) return;
            // Try to share the file; fall back to text if not supported
            let files = [];
            if (typeof navigator.canShare === 'function') {
                try {
                    const res = await fetch(url);
                    const blob = await res.blob();
                    const file = new File([blob], `quietmind-${data.card}.png`, { type: 'image/png' });
                    if (navigator.canShare({ files: [file] })) files = [file];
                } catch {}
            }
            if (files.length) {
                await navigator.share({ title: 'Quiet Mind', text: shareLine(data), files });
            } else {
                await navigator.share({ title: 'Quiet Mind', text: shareLine(data), url: appUrl });
            }
        } catch {}
    };

    return (
        <div className="fixed inset-0 z-[60] bg-forest/30 backdrop-blur-sm grid place-items-center p-3 animate-fade-in" role="dialog" data-testid="share-modal">
            <div className="w-full max-w-xl qm-card p-6 md:p-8 space-y-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">Share</p>
                        <h2 className="font-serif text-2xl text-forest mt-1">A small moment, gently shared</h2>
                    </div>
                    <button onClick={onClose} aria-label="Close" data-testid="share-close-button" className="rounded-full w-10 h-10 grid place-items-center text-forest hover:bg-dusty/40">
                        <X size={18} />
                    </button>
                </div>

                <div className="rounded-3xl overflow-hidden border border-border shadow-sm">
                    <img src={url} alt="Share card preview" className="w-full block" data-testid="share-card-image" />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {canWebShare && (
                        <button onClick={nativeShare} data-testid="share-native-button" className="qm-btn-primary inline-flex items-center gap-2">
                            <Share2 size={16} /> Share
                        </button>
                    )}
                    <button onClick={download} disabled={downloading} data-testid="share-download-button" className="qm-btn-ghost inline-flex items-center gap-2 disabled:opacity-50">
                        <Download size={16} /> {downloading ? 'Preparing…' : 'Download PNG'}
                    </button>
                    <button onClick={copy} data-testid="share-copy-button" className="qm-btn-ghost inline-flex items-center gap-2">
                        {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied' : 'Copy caption'}
                    </button>
                    <span className="ml-auto text-xs text-forest-soft hidden sm:inline">1200 × 630 · ready for any platform</span>
                </div>
            </div>
        </div>
    );
}
